import React, { useState, useCallback, useEffect, useRef } from 'react'

import { LoginForm } from '@platform/auth/components/LoginForm'
import { RegisterForm } from '@platform/auth/components/RegisterForm'
import { WaitlistCard } from '@platform/auth/components/WaitlistCard'
import { AuthModalState } from '@platform/auth/types/ui.types'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useSignupMode } from '@/platform/auth/hooks/useSignupMode'
import { useFocusTrap } from '@/shared/hooks/useFocusTrap'
import { LEGAL_URLS } from '@/shared/utils/constants'

/**
 * Authentication Modal Component
 * Reusable modal container for login and registration forms
 * Following Feature-Based Modular Architecture
 */

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  initialMode?: 'login' | 'register'
  onSuccess?: () => void
  className?: string
}

const initialModalState: AuthModalState = {
  isOpen: false,
  mode: 'login',
  isTransitioning: false,
  isLoading: false,
  error: null,
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'login',
  onSuccess,
  className = '',
}) => {
  const { t } = useTranslation(['auth', 'common'])
  // config auth/signups: anything but "open" turns the sign-up tab into the waitlist
  const { mode: signupMode } = useSignupMode()
  const waitlist = signupMode !== 'open'
  const [modalState, setModalState] = useState<AuthModalState>({
    ...initialModalState,
    mode: initialMode,
  })
  const modalRef = useRef<HTMLDivElement>(null)

  // ⚠️ Declaraba `aria-modal="true"` sin atrapar el foco: medido en la landing
  // con el modal de acceso abierto, Tab desde `auth-modal-privacy-link` (el
  // último de 12) daba `defaultPrevented: false` y salía a la página de detrás,
  // con el formulario de credenciales aún abierto.
  //
  // `isOpen`, no `true`: este modal está siempre montado y sale por
  // `if (!isOpen) return null`.
  useFocusTrap(modalRef, isOpen)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Update modal state when props change
  useEffect(() => {
    setModalState((prev) => ({
      ...prev,
      isOpen,
      mode: initialMode,
    }))
  }, [isOpen, initialMode])

  // Handle modal opening/closing
  useEffect(() => {
    if (isOpen) {
      // Store currently focused element
      previousFocusRef.current = document.activeElement as HTMLElement

      // Prevent body scroll
      document.body.style.overflow = 'hidden'

      // Focus modal
      setTimeout(() => {
        modalRef.current?.focus()
      }, 100)
    } else {
      // Restore body scroll
      document.body.style.overflow = ''

      // Restore focus
      if (previousFocusRef.current) {
        previousFocusRef.current.focus()
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
      return () => {
        document.removeEventListener('keydown', handleEscapeKey)
      }
    }

    // Return undefined explicitly for the case when !isOpen
    return undefined
  }, [isOpen, onClose])

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose],
  )

  // Switch between login and register
  const switchMode = useCallback((mode: 'login' | 'register') => {
    setModalState((prev) => ({ ...prev, mode }))
  }, [])

  // Handle form success
  const handleFormSuccess = useCallback(() => {
    onSuccess?.()
    onClose()
  }, [onSuccess, onClose])

  // Don't render if not open
  if (!isOpen) {
    return null
  }

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      data-testid={modalState.mode === 'login' ? 'login-modal' : 'register-modal'}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        onClick={handleBackdropClick}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        role="button"
        tabIndex={0}
        data-testid="auth-modal-backdrop"
        aria-label={t('common:aria.closeModal')}
      />

      {/* Modal Content */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          tabIndex={-1}
          className="relative z-50 w-full max-w-md transform rounded-lg bg-crypto-dark border border-crypto-border shadow-xl transition-all max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="auth-modal-content"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-crypto-border">
            <div>
              <h2
                id="auth-modal-title"
                className="text-xl font-bold text-crypto-text"
                data-testid="auth-modal-title"
              >
                {modalState.mode === 'login' ? t('modal.loginTitle') : t('modal.registerTitle')}
              </h2>
              <p className="mt-1 text-sm text-crypto-muted">
                {modalState.mode === 'login'
                  ? t('modal.loginDescription')
                  : t('modal.registerDescription')}
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-crypto-muted hover:text-crypto-text transition-colors p-1 rounded focus:outline-none focus:ring-2 focus:ring-crypto-accent"
              aria-label={t('modal.closeModal')}
              data-testid="auth-modal-close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Mode Tabs */}
          <div className="flex border-b border-crypto-border">
            <button
              onClick={() => switchMode('login')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-crypto-accent ${
                modalState.mode === 'login'
                  ? 'text-crypto-accent border-b-2 border-crypto-accent bg-crypto-dark-lighter'
                  : 'text-crypto-muted hover:text-crypto-text'
              }`}
              data-testid="auth-modal-login-tab"
            >
              {t('actions.sign_in')}
            </button>
            <button
              onClick={() => switchMode('register')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-crypto-accent ${
                modalState.mode === 'register'
                  ? 'text-crypto-accent border-b-2 border-crypto-accent bg-crypto-dark-lighter'
                  : 'text-crypto-muted hover:text-crypto-text'
              }`}
              data-testid="auth-modal-register-tab"
            >
              {waitlist ? t('waitlist.cta') : t('actions.sign_up')}
            </button>
          </div>

          {/* Form Content */}
          <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-crypto-border scrollbar-track-crypto-dark">
            <div className="p-6">
              {modalState.mode === 'login' ? (
                <LoginForm
                  onSuccess={handleFormSuccess}
                  onSwitchToRegister={() => switchMode('register')}
                  showSwitchToRegister={false} // We handle switching with tabs
                />
              ) : waitlist ? (
                <WaitlistCard source="landing" />
              ) : (
                <RegisterForm
                  onSuccess={handleFormSuccess}
                  onSwitchToLogin={() => switchMode('login')}
                  showSwitchToLogin={false} // We handle switching with tabs
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className="px-6 py-4 bg-crypto-dark-lighter border-t border-crypto-border"
            data-testid="auth-modal-footer"
          >
            <p className="text-xs text-crypto-muted text-center">
              {t('modal.termsAgreement')}{' '}
              <button
                className="text-crypto-accent hover:text-crypto-accent-light transition-colors underline"
                onClick={() => {
                  window.open(LEGAL_URLS.TERMS, '_blank', 'noopener,noreferrer')
                }}
                data-testid="auth-modal-terms-link"
                aria-label={t('modal.termsOfService')}
              >
                {t('modal.termsOfService')}
              </button>{' '}
              {t('modal.and')}{' '}
              <button
                className="text-crypto-accent hover:text-crypto-accent-light transition-colors underline"
                onClick={() => {
                  window.open(LEGAL_URLS.PRIVACY, '_blank', 'noopener,noreferrer')
                }}
                data-testid="auth-modal-privacy-link"
                aria-label={t('modal.privacyPolicy')}
              >
                {t('modal.privacyPolicy')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
